import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { savePresentationData, generateShortLink } from '../utils/presentationLinks';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

export default function ApresentacaoConfig() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    nomeCliente: '',
    nomeEscritorio: '',
    contatoWhatsapp: '',
    precoPix: '22700',
    precoParcelado: '26500',
    parcelas: '15',
    mensalidade: 'Dependerá do uso e da complexidade das ferramentas usadas'
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const formatCurrency = (value: string) => {
    const numValue = parseFloat(value.replace(/\D/g, ''));
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(numValue / 100);
  };

  const generateLink = () => {
    // Salva os dados e gera um código único
    const code = savePresentationData({
      nome: formData.nomeCliente,
      escritorio: formData.nomeEscritorio,
      contato: formData.contatoWhatsapp,
      precoPix: formData.precoPix,
      precoParcelado: formData.precoParcelado,
      parcelas: formData.parcelas,
      mensalidade: formData.mensalidade
    });
    
    // Gera o link curto
    return generateShortLink(code);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generateLink());
      alert('Link copiado para a área de transferência!');
    } catch (err) {
      console.error('Erro ao copiar:', err);
    }
  };

  const previewApresentacao = () => {
    window.open(generateLink(), '_blank');
  };

  return (
    <div className="min-h-screen bg-[#081428] text-white">
      <Navbar />
      
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Configurar Apresentação
            </h1>
            <p className="text-xl text-gray-300">
              Personalize os parâmetros da apresentação do Escritório Inteligente
            </p>
          </div>

          {/* Dados do Cliente */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 mb-8 group hover:bg-white/15 transition-all duration-500 border border-transparent hover:border-blue-500/30 hover:shadow-lg hover:shadow-blue-500/10">
            <h2 className="text-2xl font-bold mb-6 text-center text-blue-400 group-hover:text-blue-300 transition-colors duration-300">
              <span className="inline-block group-hover:scale-105 transition-transform duration-300">👤</span> Dados do Cliente
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="group/input">
                <label className="block text-sm font-medium mb-2 group-hover/input:text-blue-300 transition-colors duration-300">
                  Nome do Cliente *
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="nomeCliente"
                    value={formData.nomeCliente}
                    onChange={handleInputChange}
                    placeholder="Ex: Dr. João Silva"
                    className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 group-hover/input:bg-white/25 group-hover/input:border-blue-400/50 transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/20"
                    required
                  />
                  <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-blue-500/0 to-blue-500/0 group-hover/input:from-blue-500/5 group-hover/input:to-blue-500/10 transition-all duration-300 pointer-events-none"></div>
                </div>
              </div>

              <div className="group/input">
                <label className="block text-sm font-medium mb-2 group-hover/input:text-blue-300 transition-colors duration-300">
                  Nome do Escritório *
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="nomeEscritorio"
                    value={formData.nomeEscritorio}
                    onChange={handleInputChange}
                    placeholder="Ex: Silva & Associados"
                    className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 group-hover/input:bg-white/25 group-hover/input:border-blue-400/50 transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/20"
                    required
                  />
                  <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-blue-500/0 to-blue-500/0 group-hover/input:from-blue-500/5 group-hover/input:to-blue-500/10 transition-all duration-300 pointer-events-none"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Dados do Colaborador */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 mb-8 group hover:bg-white/15 transition-all duration-500 border border-transparent hover:border-green-500/30 hover:shadow-lg hover:shadow-green-500/10">
            <h2 className="text-2xl font-bold mb-6 text-center text-green-400 group-hover:text-green-300 transition-colors duration-300">
              <span className="inline-block group-hover:scale-105 transition-transform duration-300">📱</span> Dados do Colaborador
            </h2>
            
            <div className="max-w-md mx-auto">
              <div className="group/input">
                <label className="block text-sm font-medium mb-2 group-hover/input:text-green-300 transition-colors duration-300">
                  Seu WhatsApp para Contato *
                </label>
                <div className="relative">
                  <input
                    type="tel"
                    name="contatoWhatsapp"
                    value={formData.contatoWhatsapp}
                    onChange={handleInputChange}
                    placeholder="Ex: 84999869793"
                    className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 group-hover/input:bg-white/25 group-hover/input:border-green-400/50 transition-all duration-300 hover:shadow-lg hover:shadow-green-500/20"
                    required
                  />
                  <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-green-500/0 to-green-500/0 group-hover/input:from-green-500/5 group-hover/input:to-green-500/10 transition-all duration-300 pointer-events-none"></div>
                </div>
                <p className="text-xs text-gray-400 mt-1 group-hover/input:text-gray-300 transition-colors duration-300">Apenas números, sem espaços ou caracteres especiais</p>
                <p className="text-xs text-green-400 mt-2 group-hover/input:text-green-300 transition-colors duration-300">Este será o número que receberá as mensagens do cliente</p>
              </div>
            </div>
          </div>

          {/* Parâmetros da Proposta */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 mb-8 group hover:bg-white/15 transition-all duration-500 border border-transparent hover:border-yellow-500/30 hover:shadow-lg hover:shadow-yellow-500/10">
            <h2 className="text-2xl font-bold mb-6 text-center text-yellow-400 group-hover:text-yellow-300 transition-colors duration-300">
              <span className="inline-block group-hover:scale-105 transition-transform duration-300">💰</span> Parâmetros da Proposta
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="group/input">
                <label className="block text-sm font-medium mb-2 group-hover/input:text-yellow-300 transition-colors duration-300">
                  Preço à Vista (PIX) *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 group-hover/input:text-yellow-400 transition-colors duration-300">R$</span>
                  <input
                    type="text"
                    name="precoPix"
                    value={formatCurrency(formData.precoPix)}
                    onChange={(e) => {
                      const numericValue = e.target.value.replace(/\D/g, '');
                      setFormData(prev => ({ ...prev, precoPix: numericValue }));
                    }}
                    className="w-full pl-8 pr-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 group-hover/input:bg-white/25 group-hover/input:border-yellow-400/50 transition-all duration-300 hover:shadow-lg hover:shadow-yellow-500/20"
                    placeholder="22.700,00"
                  />
                  <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-yellow-500/0 to-yellow-500/0 group-hover/input:from-yellow-500/5 group-hover/input:to-yellow-500/10 transition-all duration-300 pointer-events-none"></div>
                </div>
              </div>

              <div className="group/input">
                <label className="block text-sm font-medium mb-2 group-hover/input:text-yellow-300 transition-colors duration-300">
                  Preço Parcelado *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 group-hover/input:text-yellow-400 transition-colors duration-300">R$</span>
                  <input
                    type="text"
                    name="precoParcelado"
                    value={formatCurrency(formData.precoParcelado)}
                    onChange={(e) => {
                      const numericValue = e.target.value.replace(/\D/g, '');
                      setFormData(prev => ({ ...prev, precoParcelado: numericValue }));
                    }}
                    className="w-full pl-8 pr-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 group-hover/input:bg-white/25 group-hover/input:border-yellow-400/50 transition-all duration-300 hover:shadow-lg hover:shadow-yellow-500/20"
                    placeholder="26.500,00"
                  />
                  <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-yellow-500/0 to-yellow-500/0 group-hover/input:from-yellow-500/5 group-hover/input:to-yellow-500/10 transition-all duration-300 pointer-events-none"></div>
                </div>
              </div>

              <div className="group/input">
                <label className="block text-sm font-medium mb-2 group-hover/input:text-yellow-300 transition-colors duration-300">
                  Número de Parcelas *
                </label>
                <div className="relative">
                  <input
                    type="number"
                    name="parcelas"
                    value={formData.parcelas}
                    onChange={handleInputChange}
                    min="1"
                    max="24"
                    className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 group-hover/input:bg-white/25 group-hover/input:border-yellow-400/50 transition-all duration-300 hover:shadow-lg hover:shadow-yellow-500/20"
                    placeholder="15"
                  />
                  <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-yellow-500/0 to-yellow-500/0 group-hover/input:from-yellow-500/5 group-hover/input:to-yellow-500/10 transition-all duration-300 pointer-events-none"></div>
                </div>
              </div>
            </div>

            <div className="mt-6 group/input">
              <label className="block text-sm font-medium mb-2 group-hover/input:text-yellow-300 transition-colors duration-300">
                Observação sobre Mensalidade *
              </label>
              <div className="relative">
                <textarea
                  name="mensalidade"
                  value={formData.mensalidade}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 group-hover/input:bg-white/25 group-hover/input:border-yellow-400/50 transition-all duration-300 hover:shadow-lg hover:shadow-yellow-500/20 resize-none"
                  placeholder="Dependerá do uso e da complexidade das ferramentas usadas"
                />
                <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-yellow-500/0 to-yellow-500/0 group-hover/input:from-yellow-500/5 group-hover/input:to-yellow-500/10 transition-all duration-300 pointer-events-none"></div>
              </div>
            </div>
          </div>

          {/* Preview Section */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 mb-8 group hover:bg-white/15 transition-all duration-500 border border-transparent hover:border-purple-500/30 hover:shadow-lg hover:shadow-purple-500/10">
            <h2 className="text-2xl font-bold mb-6 text-center group-hover:text-purple-300 transition-colors duration-300">
              <span className="inline-block group-hover:scale-105 transition-transform duration-300">👁️</span> Preview da Apresentação
            </h2>
            
            <div className="bg-white/5 rounded-lg p-6 mb-6 group-hover:bg-white/10 transition-all duration-300">
              <h3 className="text-lg font-semibold mb-4 group-hover:text-purple-300 transition-colors duration-300">Parâmetros Configurados:</h3>
              
              <div className="grid md:grid-cols-2 gap-6 text-sm">
                <div className="group/item hover:bg-white/5 rounded-lg p-3 transition-all duration-300">
                  <h4 className="font-semibold text-blue-400 mb-2 group-hover/item:text-blue-300 transition-colors duration-300">Dados do Cliente:</h4>
                  <p className="group-hover/item:text-white transition-colors duration-300"><strong>Nome:</strong> {formData.nomeCliente || 'Não informado'}</p>
                  <p className="group-hover/item:text-white transition-colors duration-300"><strong>Escritório:</strong> {formData.nomeEscritorio || 'Não informado'}</p>
                </div>
                
                <div className="group/item hover:bg-white/5 rounded-lg p-3 transition-all duration-300">
                  <h4 className="font-semibold text-green-400 mb-2 group-hover/item:text-green-300 transition-colors duration-300">Dados do Colaborador:</h4>
                  <p className="group-hover/item:text-white transition-colors duration-300"><strong>WhatsApp:</strong> {formData.contatoWhatsapp || 'Não informado'}</p>
                </div>
                
                <div className="group/item hover:bg-white/5 rounded-lg p-3 transition-all duration-300">
                  <h4 className="font-semibold text-yellow-400 mb-2 group-hover/item:text-yellow-300 transition-colors duration-300">Proposta:</h4>
                  <p className="group-hover/item:text-white transition-colors duration-300"><strong>Preço PIX:</strong> {formatCurrency(formData.precoPix)}</p>
                  <p className="group-hover/item:text-white transition-colors duration-300"><strong>Preço Parcelado:</strong> {formatCurrency(formData.precoParcelado)} em {formData.parcelas}x</p>
                </div>
                
                <div className="group/item hover:bg-white/5 rounded-lg p-3 transition-all duration-300">
                  <h4 className="font-semibold text-yellow-400 mb-2 group-hover/item:text-yellow-300 transition-colors duration-300">Observações:</h4>
                  <p className="group-hover/item:text-white transition-colors duration-300"><strong>Mensalidade:</strong> {formData.mensalidade}</p>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={previewApresentacao}
                className="group/btn px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 rounded-lg font-semibold transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-blue-500/30 border border-blue-500/30"
              >
                <span className="group-hover/btn:scale-110 transition-transform duration-300 inline-block">👁️</span> Visualizar Apresentação
              </button>
              <button
                onClick={copyToClipboard}
                className="group/btn px-8 py-3 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 rounded-lg font-semibold transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-green-500/30 border border-green-500/30"
              >
                <span className="group-hover/btn:scale-110 transition-transform duration-300 inline-block">📋</span> Copiar Link
              </button>
            </div>
          </div>

          {/* Generated Link Display */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 group hover:bg-white/15 transition-all duration-500 border border-transparent hover:border-cyan-500/30 hover:shadow-lg hover:shadow-cyan-500/10">
            <h2 className="text-2xl font-bold mb-4 text-center group-hover:text-cyan-300 transition-colors duration-300">
              <span className="inline-block group-hover:scale-105 transition-transform duration-300">🔗</span> Link Gerado
            </h2>
            <div className="bg-black/30 rounded-lg p-4 mb-4 group-hover:bg-black/40 transition-all duration-300 border border-gray-700 group-hover:border-cyan-500/30">
              <code className="text-sm break-all text-green-400 group-hover:text-cyan-300 transition-colors duration-300">
                {generateLink()}
              </code>
            </div>
            <p className="text-center text-gray-300 text-sm group-hover:text-cyan-200 transition-colors duration-300">
              Use este link para compartilhar a apresentação personalizada
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
